import { Checkbox } from '@src/common/components/Checkbox';
import type { ReactNode } from 'react';
import type { Control, FieldValues, Path, PathValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form/dist/types/errors';
import type { UseFormRegister } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

interface Props<T extends FieldValues> {
  register: UseFormRegister<T>;
  errors?: FieldErrors<T>;
  fieldName: Path<T>;
  defaultChecked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  hideLabel?: boolean;
  className?: string;
  onChange?: () => void;
}

export const FormCheckbox = <T extends FieldValues>({
  register,
  errors,
  fieldName,
  disabled,
  readOnly,
  defaultChecked,
  hideLabel,
  className,
  onChange,
}: Props<T>) => {
  const { t } = useTranslation();
  const fieldProps = register(fieldName);

  return (
    <Checkbox
      {...fieldProps}
      onChange={(event) => {
        fieldProps.onChange(event);
        onChange?.();
      }}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
      defaultChecked={defaultChecked}
      label={t(`fields.${fieldName}`)}
      hideLabel={hideLabel}
      error={
        errors &&
        errors[fieldName] &&
        t(`formErrors.${errors[fieldName]!.message}`)
      }
    />
  );
};

interface ControllerProps<T extends FieldValues> {
  control: Control<T>;
  fieldName: Path<T>;
  disabled?: boolean;
  label?: ReactNode;
  ariaLabel?: string;
  tabIndex?: number;
}

export const ControllerFormCheckbox = <T extends FieldValues>({
  control,
  fieldName,
  disabled,
  label,
  ariaLabel,
  tabIndex,
}: ControllerProps<T>) => {
  const { t } = useTranslation();

  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={false as PathValue<T, Path<T>>}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error },
        formState: { disabled: formDisabled },
      }) => (
        <Checkbox
          {...field}
          tabIndex={tabIndex}
          checked={value || false}
          onChange={(e) => {
            onChange(e.target.checked);
          }}
          disabled={disabled || formDisabled}
          label={label ?? t(`fields.${fieldName}`)}
          error={error && t(`formErrors.${error!.message}`)}
          inputProps={{ 'aria-label': ariaLabel }}
        />
      )}
    />
  );
};
